import { Injectable, computed, inject, signal } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpEventType,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  CambioCampoSplit,
  CatalogosSplitsMml,
  ColaboradorSplitEntity,
  ContratoSplitCompleto,
  ContratoSplitEntity,
  EventoTrazabilidadEntity,
  ParticipanteDirectorioEntity,
  VersionSplitEntity,
  normalizarNumeroDocumento,
  normalizarRolSplitOficial,
  normalizarTipoDocumento,
  generarCodigoSesionConsecutivo
} from '../core/models/splits.model';
import { SplitsPdfExporterService } from '../core/domain/splits-pdf-exporter.service';

export interface EstadoSplitsPersistido {
  contratos: ContratoSplitEntity[];
  colaboradores: ColaboradorSplitEntity[];
  directorioParticipantes: ParticipanteDirectorioEntity[];
  versionesPorContrato: Record<string, VersionSplitEntity[]>;
  trazabilidadPorContrato: Record<string, EventoTrazabilidadEntity[]>;
  contratosCompletosPorId: Record<string, ContratoSplitCompleto>;
  catalogos?: CatalogosSplitsMml;
  actualizadoEnIso?: string;
}

export interface SnapshotVersionPendienteSplits {
  id: string;
  contratoId: string;
  markdown: string;
  autor: string;
  motivo: string;
  archivoUrl?: string;
  storageKey?: string;
  nombreArchivo?: string;
  tamanoBytes?: number;
  idArchivoCola?: string;
  pendienteSubidaS3?: boolean;
  cambios?: CambioCampoSplit[];
  creadoEn: string;
}

export interface ArchivoS3PendienteEnCola {
  id: string;
  contratoId: string;
  carpeta: 'mml-splits/firmados' | 'mml-splits/versiones';
  nombreArchivo: string;
  tipoMime: string;
  tamanoBytes: number;
  blob?: Blob;
  arrayBuffer?: ArrayBuffer;
  dataBase64?: string;
  creadoEnIso: string;
}

export interface ProgresoSubidaDocumentoS3 {
  activo: boolean;
  porcentaje: number;
  nombreArchivo: string;
  tamanoBytes: number;
  etapa: 'preparando' | 'subiendo' | 'en_cola' | 'completado' | 'error';
  mensaje: string;
}

export interface ResultadoSubidaDocumentoS3 {
  subido: boolean;
  url: string;
  storageKey?: string;
  pendienteSubidaS3: boolean;
  idArchivoCola?: string;
  dataBase64?: string;
}

export interface OperacionesSincronizacionSplits {
  contratosUpsert: Record<string, ContratoSplitCompleto>;
  contratosEliminarIds: string[];
  contratosRestaurarIds: string[];
  versionesCrear: SnapshotVersionPendienteSplits[];
  eventosTrazabilidad: EventoTrazabilidadEntity[];
  directorioUpsert: Record<string, ParticipanteDirectorioEntity>;
  directorioEliminarIds: string[];
}

export interface ItemColaSplitsSincronizacion {
  id: string;
  creadoEnIso: string;
  actualizadoEnIso: string;
  intentos: number;
  ultimoIntentoIso?: string;
  ultimoError?: string;
  operaciones: OperacionesSincronizacionSplits;
}

export interface ResumenItemColaSplitsUi {
  id: string;
  etiqueta: string;
  detalleOperaciones: string[];
  contratosIds: string[];
  intentos: number;
  creadoEnIso: string;
  actualizadoEnIso: string;
  ultimoIntentoIso?: string;
  ultimoError?: string;
}

export interface EventoSincronizacionColaSplits {
  id: string;
  timestamp: string;
  tipo:
    | 'ENCOLADO_OFFLINE'
    | 'ENCOLADO_ONLINE'
    | 'SINCRONIZADO_OK'
    | 'ERROR_REINTENTO'
    | 'MODO_OFFLINE'
    | 'CONEXION_RESTABLECIDA';
  titulo: string;
  detalle: string;
  contratoId?: string;
}

export type EstadoSincronizacionSplitsUi =
  | 'sincronizado'
  | 'sincronizando'
  | 'pendiente'
  | 'offline'
  | 'offline_con_cola'
  | 'error';

const IDB_NAME = 'mj_splits_mml_idb_v2';
const IDB_VERSION = 1;
const STORE_SNAPSHOT = 'snapshot';
const STORE_OUTBOX = 'outbox';
const STORE_ARCHIVOS_S3 = 'archivos_s3_pendientes';
const KEY_SNAPSHOT_MAIN = 'estado_principal_splits_v2';
const KEY_OUTBOX_MAIN = 'cola_pendiente_splits_v2';
const LS_OUTBOX_KEY = 'mj_splits_mml_outbox_v2';
const LS_TRACE_SYNC_KEY = 'mj_splits_mml_sync_trace_v2';

const REQUEST_TIMEOUT_MS = 20_000;
const UPLOAD_S3_TIMEOUT_MS = 45_000;
const BASE_RETRY_MS = 4_000;
const MAX_RETRY_MS = 60_000;

export function crearOperacionesSplitsVacias(): OperacionesSincronizacionSplits {
  return {
    contratosUpsert: {},
    contratosEliminarIds: [],
    contratosRestaurarIds: [],
    versionesCrear: [],
    eventosTrazabilidad: [],
    directorioUpsert: {},
    directorioEliminarIds: []
  };
}

export function esOperacionesSplitsVacias(op: OperacionesSincronizacionSplits): boolean {
  return (
    Object.keys(op.contratosUpsert || {}).length === 0 &&
    (op.contratosEliminarIds?.length || 0) === 0 &&
    (op.contratosRestaurarIds?.length || 0) === 0 &&
    (op.versionesCrear?.length || 0) === 0 &&
    (op.eventosTrazabilidad?.length || 0) === 0 &&
    Object.keys(op.directorioUpsert || {}).length === 0 &&
    (op.directorioEliminarIds?.length || 0) === 0
  );
}

@Injectable({
  providedIn: 'root'
})
export class ColaSincronizacionSplitsService {
  private readonly http = inject(HttpClient);
  private readonly pdfExporter = inject(SplitsPdfExporterService);
  private readonly cacheUrlsS3SubidasPorColaId = new Map<string, { urlCompleta: string; storageKey: string }>();
  private reparandoHuerfanosS3 = false;
  private readonly apiUrl = environment.URL_SERVER.endsWith('/')
    ? `${environment.URL_SERVER}mml-splits`
    : `${environment.URL_SERVER}/mml-splits`;
  private readonly uploadFileUrl = environment.URL_SERVER.endsWith('/')
    ? `${environment.URL_SERVER}upload-file`
    : `${environment.URL_SERVER}/upload-file`;
  private readonly s3BaseUrl = environment.URL_S3 || 'https://d3q0kt5yih1b7n.cloudfront.net/';
  private readonly S3_BUCKET = 'medellinjoven-production';

  private dbPromise: Promise<IDBDatabase | null> | null = null;
  private retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private countdownIntervalId: ReturnType<typeof setInterval> | null = null;
  private procesando = false;
  private listenersPostSync: Array<() => void> = [];

  // Estado reactivo para la UI y observabilidad
  private readonly colaInterna = signal<ItemColaSplitsSincronizacion[]>([]);
  readonly enLinea = signal<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true);
  readonly sincronizando = signal<boolean>(false);
  readonly ultimoError = signal<string | null>(null);
  readonly segundosParaReintento = signal<number>(0);
  readonly ultimaSincronizacionExitosaIso = signal<string | null>(null);
  readonly historialSincronizacion = signal<EventoSincronizacionColaSplits[]>([]);

  // Progreso en tiempo real de subida de documentos a S3 (inspirado en Asistencia MML)
  readonly progresoSubidaS3 = signal<ProgresoSubidaDocumentoS3 | null>(null);

  readonly cola = this.colaInterna.asReadonly();
  readonly pendientesCount = computed(() => this.colaInterna().length);

  readonly totalOperacionesPendientes = computed(() => {
    let total = 0;
    for (const item of this.colaInterna()) {
      const op = item.operaciones;
      total +=
        Object.keys(op.contratosUpsert || {}).length +
        (op.contratosEliminarIds?.length || 0) +
        (op.contratosRestaurarIds?.length || 0) +
        (op.versionesCrear?.length || 0) +
        (op.eventosTrazabilidad?.length || 0) +
        Object.keys(op.directorioUpsert || {}).length +
        (op.directorioEliminarIds?.length || 0);
    }
    return total;
  });

  readonly contratosPendientesIds = computed<Set<string>>(() => {
    const ids = new Set<string>();
    for (const item of this.colaInterna()) {
      const op = item.operaciones;
      for (const id of Object.keys(op.contratosUpsert || {})) {
        ids.add(id);
      }
      for (const id of op.contratosEliminarIds || []) {
        ids.add(id);
      }
      for (const id of op.contratosRestaurarIds || []) {
        ids.add(id);
      }
      for (const v of op.versionesCrear || []) {
        if (v.contratoId) ids.add(v.contratoId);
      }
      for (const ev of op.eventosTrazabilidad || []) {
        if (ev.contratoId) ids.add(ev.contratoId);
      }
    }
    return ids;
  });

  readonly itemsEnColaUi = computed<ResumenItemColaSplitsUi[]>(() =>
    this.colaInterna().map((item) => {
      const detalles = this.construirDetalleOperacionesItem(item.operaciones);
      const contratosIds = Array.from(
        new Set([
          ...Object.keys(item.operaciones.contratosUpsert || {}),
          ...(item.operaciones.contratosEliminarIds || []),
          ...(item.operaciones.contratosRestaurarIds || [])
        ])
      );
      return {
        id: item.id,
        etiqueta: this.construirEtiquetaItem(item.operaciones),
        detalleOperaciones: detalles,
        contratosIds,
        intentos: item.intentos,
        creadoEnIso: item.creadoEnIso,
        actualizadoEnIso: item.actualizadoEnIso,
        ultimoIntentoIso: item.ultimoIntentoIso,
        ultimoError: item.ultimoError
      };
    })
  );

  readonly estadoUi = computed<EstadoSincronizacionSplitsUi>(() => {
    const online = this.enLinea();
    const pendientes = this.pendientesCount();
    const sync = this.sincronizando();
    const err = this.ultimoError();

    if (!online) {
      return pendientes > 0 ? 'offline_con_cola' : 'offline';
    }
    if (sync) {
      return 'sincronizando';
    }
    if (pendientes > 0 && err) {
      return 'error';
    }
    if (pendientes > 0) {
      return 'pendiente';
    }
    return 'sincronizado';
  });

  private readonly inicializacionIdbListaPromise: Promise<void>;

  constructor() {
    this.restaurarHistorialTrazabilidadLocal();
    this.restaurarColaDesdeLocalStorage();
    this.configurarListenersConectividad();
    this.inicializacionIdbListaPromise = this.inicializarDesdeIndexedDb();
  }

  esperarInicializacionIdb(): Promise<void> {
    return this.inicializacionIdbListaPromise;
  }

  onPostSincronizacion(cb: () => void): void {
    this.listenersPostSync.push(cb);
  }

  private notificarPostSincronizacion(): void {
    for (const cb of this.listenersPostSync) {
      try {
        cb();
      } catch {
        // Ignorar errores de callback
      }
    }
  }

  tieneCambiosPendientes(): boolean {
    return this.colaInterna().length > 0;
  }

  tieneContratoPendiente(contratoId: string): boolean {
    if (!contratoId) return false;
    return this.contratosPendientesIds().has(contratoId);
  }

  // =========================================================================
  // SUBIDA DE DOCUMENTOS FIRMADOS Y VERSIONES A AWS S3 CON COLA OFFLINE
  // =========================================================================
  async subirDocumentoAS3ConCola(params: {
    contratoId: string;
    blob: Blob;
    nombreArchivo: string;
    tipoMime?: string;
    carpeta: 'mml-splits/firmados' | 'mml-splits/versiones';
    mostrarProgresoUi?: boolean;
  }): Promise<ResultadoSubidaDocumentoS3> {
    const {
      contratoId,
      blob,
      nombreArchivo,
      tipoMime = blob.type || 'application/pdf',
      carpeta,
      mostrarProgresoUi = true
    } = params;

    const nombreSanitizado = (nombreArchivo || 'Split_Sheet_EPG.pdf')
      .replace(/[^a-zA-Z0-9._-]/g, '_')
      .replace(/_+/g, '_');
    const nombreUnico = `${contratoId}_${Date.now()}_${nombreSanitizado}`;
    const idArchivoCola = `s3-split-${contratoId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this.enLinea.set(online);

    if (online) {
      try {
        if (mostrarProgresoUi) {
          this.progresoSubidaS3.set({
            activo: true,
            porcentaje: 12,
            nombreArchivo,
            tamanoBytes: blob.size,
            etapa: 'subiendo',
            mensaje: `Subiendo "${nombreArchivo}" a custodia digital...`
          });
        }

        const subido = await this.ejecutarUploadS3ConProgreso(
          blob,
          nombreUnico,
          carpeta,
          mostrarProgresoUi ? nombreArchivo : undefined
        );

        if (mostrarProgresoUi) {
          this.progresoSubidaS3.set({
            activo: true,
            porcentaje: 100,
            nombreArchivo,
            tamanoBytes: blob.size,
            etapa: 'completado',
            mensaje: `Documento resguardado correctamente.`
          });
          setTimeout(() => {
            if (this.progresoSubidaS3()?.etapa === 'completado') {
              this.progresoSubidaS3.set(null);
            }
          }, 1800);
        }

        return {
          subido: true,
          url: subido.urlCompleta,
          storageKey: subido.storageKey,
          pendienteSubidaS3: false
        };
      } catch {
        // Si falla la red durante la subida, cae de forma transparente a la cola en IndexedDB
      }
    }

    // Resguardar el archivo binario en IndexedDB (con respaldo Base64 y ArrayBuffer para iOS WebKit)
    const dataBase64 = await this.blobABase64(blob);
    let arrayBuffer: ArrayBuffer | undefined;
    try {
      arrayBuffer = await blob.arrayBuffer();
    } catch {
      arrayBuffer = undefined;
    }

    await this.guardarArchivoS3PendienteEnIdb({
      id: idArchivoCola,
      contratoId,
      carpeta,
      nombreArchivo: nombreUnico,
      tipoMime,
      tamanoBytes: blob.size,
      blob,
      arrayBuffer,
      dataBase64: dataBase64 || undefined,
      creadoEnIso: new Date().toISOString()
    });

    const blobUrlTemporal =
      typeof URL !== 'undefined' && URL.createObjectURL ? URL.createObjectURL(blob) : '';

    if (mostrarProgresoUi) {
      this.progresoSubidaS3.set({
        activo: true,
        porcentaje: 100,
        nombreArchivo,
        tamanoBytes: blob.size,
        etapa: 'en_cola',
        mensaje: `Guardado en cola en este dispositivo. Se subirá automáticamente cuando vuelva el internet.`
      });
      setTimeout(() => {
        if (this.progresoSubidaS3()?.etapa === 'en_cola') {
          this.progresoSubidaS3.set(null);
        }
      }, 2600);
    }

    return {
      subido: false,
      url: blobUrlTemporal,
      pendienteSubidaS3: true,
      idArchivoCola,
      dataBase64: dataBase64 || undefined
    };
  }

  private ejecutarUploadS3ConProgreso(
    blob: Blob,
    nombreUnico: string,
    carpeta: 'mml-splits/firmados' | 'mml-splits/versiones',
    nombreVisibleProgreso?: string
  ): Promise<{ urlCompleta: string; storageKey: string }> {
    return new Promise<{ urlCompleta: string; storageKey: string }>((resolve, reject) => {
      const formData = new FormData();
      formData.append('bucket', this.S3_BUCKET);
      formData.append('folder', carpeta);
      formData.append('file', blob, nombreUnico);

      const req = new HttpRequest('POST', this.uploadFileUrl, formData, {
        reportProgress: true
      });

      const sub = this.http
        .request<{ success?: boolean; url?: string; message?: string }>(req)
        .pipe(timeout(UPLOAD_S3_TIMEOUT_MS))
        .subscribe({
          next: (event) => {
            if (event.type === HttpEventType.UploadProgress && nombreVisibleProgreso) {
              const total = event.total || blob.size || 1;
              const pct = Math.min(95, Math.max(15, Math.round((event.loaded / total) * 90)));
              this.progresoSubidaS3.set({
                activo: true,
                porcentaje: pct,
                nombreArchivo: nombreVisibleProgreso,
                tamanoBytes: blob.size,
                etapa: 'subiendo',
                mensaje: `Subiendo "${nombreVisibleProgreso}" (${pct}%)...`
              });
            } else if (event instanceof HttpResponse) {
              const body = event.body;
              if (body?.success && body.url) {
                const storageKey = String(body.url).replace(/^\//, '');
                const urlCompleta = storageKey.startsWith('http')
                  ? storageKey
                  : `${this.s3BaseUrl.replace(/\/$/, '')}/${storageKey}`;
                sub.unsubscribe();
                resolve({ urlCompleta, storageKey });
              } else {
                sub.unsubscribe();
                reject(new Error(body?.message || 'El servidor no devolvió la ruta del documento.'));
              }
            }
          },
          error: (err) => {
            sub.unsubscribe();
            reject(err);
          }
        });
    });
  }

  private async guardarArchivoS3PendienteEnIdb(item: ArchivoS3PendienteEnCola): Promise<void> {
    const db = await this.abrirBaseDatos();
    if (!db || !db.objectStoreNames.contains(STORE_ARCHIVOS_S3)) return;
    try {
      const guardadoConBlob = await new Promise<boolean>((resolve) => {
        try {
          const tx = db.transaction(STORE_ARCHIVOS_S3, 'readwrite');
          const store = tx.objectStore(STORE_ARCHIVOS_S3);
          store.put(item);
          tx.oncomplete = () => resolve(true);
          tx.onerror = () => resolve(false);
        } catch {
          resolve(false);
        }
      });
      if (!guardadoConBlob) {
        // Fallback para iOS Safari/WebKit donde almacenar objetos Blob directos en IndexedDB falla con DataCloneError
        const itemSinBlob: ArchivoS3PendienteEnCola = { ...item };
        delete itemSinBlob.blob;
        await new Promise<void>((resolve) => {
          try {
            const tx = db.transaction(STORE_ARCHIVOS_S3, 'readwrite');
            const store = tx.objectStore(STORE_ARCHIVOS_S3);
            store.put(itemSinBlob);
            tx.oncomplete = () => resolve();
            tx.onerror = () => resolve();
          } catch {
            resolve();
          }
        });
      }
    } catch {
      // Silencioso si IndexedDB está restringido
    }
  }

  async obtenerArchivoS3PendientePorId(idArchivoCola: string): Promise<ArchivoS3PendienteEnCola | null> {
    if (!idArchivoCola) return null;
    const db = await this.abrirBaseDatos();
    if (!db || !db.objectStoreNames.contains(STORE_ARCHIVOS_S3)) return null;
    try {
      return await new Promise<ArchivoS3PendienteEnCola | null>((resolve) => {
        const tx = db.transaction(STORE_ARCHIVOS_S3, 'readonly');
        const store = tx.objectStore(STORE_ARCHIVOS_S3);
        const req = store.get(idArchivoCola);
        req.onsuccess = () => resolve((req.result as ArchivoS3PendienteEnCola) ?? null);
        req.onerror = () => resolve(null);
      });
    } catch {
      return null;
    }
  }

  private async obtenerTodosArchivosS3PendientesEnIdb(): Promise<ArchivoS3PendienteEnCola[]> {
    const db = await this.abrirBaseDatos();
    if (!db || !db.objectStoreNames.contains(STORE_ARCHIVOS_S3)) return [];
    try {
      return await new Promise<ArchivoS3PendienteEnCola[]>((resolve) => {
        const tx = db.transaction(STORE_ARCHIVOS_S3, 'readonly');
        const store = tx.objectStore(STORE_ARCHIVOS_S3);
        const req = store.getAll();
        req.onsuccess = () => resolve(Array.isArray(req.result) ? (req.result as ArchivoS3PendienteEnCola[]) : []);
        req.onerror = () => resolve([]);
      });
    } catch {
      return [];
    }
  }

  private async eliminarArchivoS3PendienteDeIdb(idArchivoCola: string): Promise<void> {
    if (!idArchivoCola) return;
    const db = await this.abrirBaseDatos();
    if (!db || !db.objectStoreNames.contains(STORE_ARCHIVOS_S3)) return;
    try {
      await new Promise<void>((resolve) => {
        const tx = db.transaction(STORE_ARCHIVOS_S3, 'readwrite');
        const store = tx.objectStore(STORE_ARCHIVOS_S3);
        store.delete(idArchivoCola);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      });
    } catch {
      // Silencioso
    }
  }

  private extraerBlobDeItemPendiente(pendiente: ArchivoS3PendienteEnCola | null): Blob | null {
    if (!pendiente) return null;
    const mime = pendiente.tipoMime || 'application/pdf';
    if (pendiente.blob instanceof Blob && pendiente.blob.size > 0) {
      return pendiente.blob;
    }
    if (pendiente.arrayBuffer && pendiente.arrayBuffer.byteLength > 0) {
      return new Blob([pendiente.arrayBuffer], { type: mime });
    }
    if (pendiente.dataBase64) {
      return this.base64ABlob(pendiente.dataBase64);
    }
    return null;
  }

  async obtenerBlobUrlLocalPendienteS3(idArchivoCola?: string): Promise<string | null> {
    if (!idArchivoCola) return null;
    const enCacheS3 = this.cacheUrlsS3SubidasPorColaId.get(idArchivoCola);
    if (enCacheS3?.urlCompleta) return enCacheS3.urlCompleta;
    const pendiente = await this.obtenerArchivoS3PendientePorId(idArchivoCola);
    const blob = this.extraerBlobDeItemPendiente(pendiente);
    if (!blob) return null;
    return URL.createObjectURL(blob);
  }

  private async blobABase64(blob: Blob): Promise<string | null> {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      const chunkSize = 8192;
      for (let i = 0; i < bytes.length; i += chunkSize) {
        const sub = bytes.subarray(i, i + chunkSize);
        binary += String.fromCharCode.apply(null, Array.from(sub));
      }
      const mime = blob.type || 'application/pdf';
      return `data:${mime};base64,${btoa(binary)}`;
    } catch {
      return null;
    }
  }

  private async resolverSubidaS3PendientePorId(
    idArchivoCola?: string,
    dataBase64Fallback?: string,
    contratoIdFallback?: string,
    nombreFallback?: string,
    carpetaFallback: 'mml-splits/firmados' | 'mml-splits/versiones' = 'mml-splits/firmados'
  ): Promise<{ urlCompleta: string; storageKey: string } | null> {
    if (idArchivoCola && this.cacheUrlsS3SubidasPorColaId.has(idArchivoCola)) {
      return this.cacheUrlsS3SubidasPorColaId.get(idArchivoCola)!;
    }

    if (idArchivoCola) {
      const pendiente = await this.obtenerArchivoS3PendientePorId(idArchivoCola);
      const blobRecuperado = this.extraerBlobDeItemPendiente(pendiente);
      if (pendiente && blobRecuperado) {
        const res = await this.ejecutarUploadS3ConProgreso(
          blobRecuperado,
          pendiente.nombreArchivo,
          pendiente.carpeta
        );
        this.cacheUrlsS3SubidasPorColaId.set(idArchivoCola, res);
        await this.eliminarArchivoS3PendienteDeIdb(idArchivoCola);
        return res;
      }
    }

    if (dataBase64Fallback && dataBase64Fallback.startsWith('data:')) {
      const blob = this.base64ABlob(dataBase64Fallback);
      if (blob) {
        const safeName = (nombreFallback || 'Split_Sheet_EPG.pdf')
          .replace(/[^a-zA-Z0-9._-]/g, '_')
          .replace(/_+/g, '_');
        const nombreUnico = `${contratoIdFallback || 'split'}_${Date.now()}_${safeName}`;
        const res = await this.ejecutarUploadS3ConProgreso(blob, nombreUnico, carpetaFallback);
        if (idArchivoCola) {
          this.cacheUrlsS3SubidasPorColaId.set(idArchivoCola, res);
        }
        return res;
      }
    }

    return null;
  }

  private base64ABlob(dataUri: string): Blob | null {
    try {
      const parts = dataUri.split(';base64,');
      const mime = parts[0].replace('data:', '') || 'application/pdf';
      const raw = atob(parts[1] || parts[0]);
      const arr = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) {
        arr[i] = raw.charCodeAt(i);
      }
      return new Blob([arr], { type: mime });
    } catch {
      return null;
    }
  }

  // =========================================================================
  // TRAZABILIDAD DE COLA Y AUDITORÍA DE SINCRONIZACIÓN
  // =========================================================================
  registrarTrazaCola(
    tipo: EventoSincronizacionColaSplits['tipo'],
    titulo: string,
    detalle: string,
    contratoId?: string
  ): void {
    const nuevo: EventoSincronizacionColaSplits = {
      id: `sync-split-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      timestamp: new Date().toISOString(),
      tipo,
      titulo,
      detalle,
      contratoId
    };
    const lista = [nuevo, ...this.historialSincronizacion()].slice(0, 60);
    this.historialSincronizacion.set(lista);
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.setItem(LS_TRACE_SYNC_KEY, JSON.stringify(lista));
      } catch {
        // Ignorar cuota de localStorage
      }
    }
  }

  private restaurarHistorialTrazabilidadLocal(): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      const raw = window.localStorage.getItem(LS_TRACE_SYNC_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        this.historialSincronizacion.set(parsed.slice(0, 60));
      }
    } catch {
      // Ignorar errores de parseo
    }
  }

  // =========================================================================
  // DETECCIÓN DE RED Y EVENTOS DEL NAVEGADOR (SIN SPAM CUANDO NO HAY INTERNET)
  // =========================================================================
  private configurarListenersConectividad(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('online', () => {
      this.enLinea.set(true);
      this.ultimoError.set(null);
      this.limpiarTimersReintento();
      if (this.tieneCambiosPendientes()) {
        this.registrarTrazaCola(
          'CONEXION_RESTABLECIDA',
          'Conexión a internet restablecida',
          `Subiendo automáticamente ${this.pendientesCount()} cambio(s) pendiente(s).`
        );
      }
      void this.procesarCola(true);
    });

    window.addEventListener('offline', () => {
      this.enLinea.set(false);
      this.limpiarTimersReintento();
      this.registrarTrazaCola(
        'MODO_OFFLINE',
        'Modo sin internet activado',
        'Tus cambios se guardarán en cola en este dispositivo y se subirán automáticamente cuando vuelva el internet.'
      );
    });

    window.addEventListener('focus', () => {
      const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
      this.enLinea.set(online);
      if (online && this.tieneCambiosPendientes() && !this.procesando) {
        void this.procesarCola(false);
      }
    });

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
          this.enLinea.set(online);
          if (online && this.tieneCambiosPendientes() && !this.procesando) {
            void this.procesarCola(false);
          }
        }
      });
    }
  }

  // =========================================================================
  // INDEXEDDB: SNAPSHOT OFFLINE COMPLETO + OUTBOX + ARCHIVOS S3 PENDIENTES
  // =========================================================================
  private abrirBaseDatos(): Promise<IDBDatabase | null> {
    if (typeof window === 'undefined' || !('indexedDB' in window)) {
      return Promise.resolve(null);
    }
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = new Promise<IDBDatabase | null>((resolve) => {
      try {
        const req = window.indexedDB.open(IDB_NAME, IDB_VERSION);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(STORE_SNAPSHOT)) {
            db.createObjectStore(STORE_SNAPSHOT);
          }
          if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
            db.createObjectStore(STORE_OUTBOX);
          }
          if (!db.objectStoreNames.contains(STORE_ARCHIVOS_S3)) {
            db.createObjectStore(STORE_ARCHIVOS_S3, { keyPath: 'id' });
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });

    return this.dbPromise;
  }

  private async inicializarDesdeIndexedDb(): Promise<void> {
    const db = await this.abrirBaseDatos();
    if (!db) {
      if (this.enLinea() && this.tieneCambiosPendientes()) {
        void this.procesarCola(true);
      }
      return;
    }

    try {
      const colaIdb = await new Promise<ItemColaSplitsSincronizacion[] | null>((resolve) => {
        const tx = db.transaction(STORE_OUTBOX, 'readonly');
        const store = tx.objectStore(STORE_OUTBOX);
        const req = store.get(KEY_OUTBOX_MAIN);
        req.onsuccess = () =>
          resolve(Array.isArray(req.result) ? (req.result as ItemColaSplitsSincronizacion[]) : null);
        req.onerror = () => resolve(null);
      });

      if (colaIdb && colaIdb.length > 0) {
        const combinada = this.combinarColas(this.colaInterna(), colaIdb);
        this.colaInterna.set(combinada);
        this.guardarColaEnLocalStorage(combinada);
      } else if (this.colaInterna().length > 0) {
        await this.guardarColaEnIndexedDb(this.colaInterna());
      }
    } catch {
      // Continuar con copia en memoria
    }

    if (this.enLinea()) {
      void this.procesarCola(true);
    }
  }

  async guardarSnapshotOffline(estado: EstadoSplitsPersistido): Promise<void> {
    const db = await this.abrirBaseDatos();
    if (!db) return;
    try {
      await new Promise<void>((resolve) => {
        const tx = db.transaction(STORE_SNAPSHOT, 'readwrite');
        const store = tx.objectStore(STORE_SNAPSHOT);
        store.put(
          {
            ...estado,
            actualizadoEnIso: new Date().toISOString()
          },
          KEY_SNAPSHOT_MAIN
        );
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      });
    } catch {
      // Silencioso si IndexedDB está restringido
    }
  }

  async cargarSnapshotOffline(): Promise<EstadoSplitsPersistido | null> {
    const db = await this.abrirBaseDatos();
    if (!db) return null;
    try {
      return await new Promise<EstadoSplitsPersistido | null>((resolve) => {
        const tx = db.transaction(STORE_SNAPSHOT, 'readonly');
        const store = tx.objectStore(STORE_SNAPSHOT);
        const req = store.get(KEY_SNAPSHOT_MAIN);
        req.onsuccess = () => resolve((req.result as EstadoSplitsPersistido) ?? null);
        req.onerror = () => resolve(null);
      });
    } catch {
      return null;
    }
  }

  private restaurarColaDesdeLocalStorage(): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      const raw = window.localStorage.getItem(LS_OUTBOX_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const validos = parsed.filter(
          (it: ItemColaSplitsSincronizacion) =>
            it && it.id && it.operaciones && !esOperacionesSplitsVacias(it.operaciones)
        );
        this.colaInterna.set(validos);
      }
    } catch {
      // Ignorar errores de parseo
    }
  }

  private guardarColaEnLocalStorage(cola: ItemColaSplitsSincronizacion[]): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      if (cola.length === 0) {
        window.localStorage.removeItem(LS_OUTBOX_KEY);
      } else {
        window.localStorage.setItem(LS_OUTBOX_KEY, JSON.stringify(cola));
      }
    } catch {
      // Si el payload excede la cuota de localStorage, IndexedDB conserva el payload íntegro.
    }
  }

  private async guardarColaEnIndexedDb(cola: ItemColaSplitsSincronizacion[]): Promise<void> {
    const db = await this.abrirBaseDatos();
    if (!db) return;
    try {
      await new Promise<void>((resolve) => {
        const tx = db.transaction(STORE_OUTBOX, 'readwrite');
        const store = tx.objectStore(STORE_OUTBOX);
        if (cola.length === 0) {
          store.delete(KEY_OUTBOX_MAIN);
        } else {
          store.put(cola, KEY_OUTBOX_MAIN);
        }
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      });
    } catch {
      // Ignorar errores de escritura en IndexedDB
    }
  }

  private persistirCola(cola: ItemColaSplitsSincronizacion[]): void {
    this.colaInterna.set(cola);
    this.guardarColaEnLocalStorage(cola);
    void this.guardarColaEnIndexedDb(cola);
  }

  private combinarColas(
    actual: ItemColaSplitsSincronizacion[],
    desdeIdb: ItemColaSplitsSincronizacion[]
  ): ItemColaSplitsSincronizacion[] {
    const mapa = new Map<string, ItemColaSplitsSincronizacion>();
    for (const item of desdeIdb) {
      if (item?.id && item.operaciones && !esOperacionesSplitsVacias(item.operaciones)) {
        mapa.set(item.id, item);
      }
    }
    for (const item of actual) {
      if (item?.id && item.operaciones && !esOperacionesSplitsVacias(item.operaciones)) {
        const existente = mapa.get(item.id);
        if (!existente || (item.actualizadoEnIso || '') > (existente.actualizadoEnIso || '')) {
          mapa.set(item.id, item);
        }
      }
    }
    return Array.from(mapa.values()).sort((a, b) => a.creadoEnIso.localeCompare(b.creadoEnIso));
  }

  // =========================================================================
  // ENCOLADO CON FUSIÓN INTELIGENTE (COALESCING) POR SPLIT SHEET / ENTIDAD
  // =========================================================================
  encolarGuardarContrato(agregado: ContratoSplitCompleto): void {
    const clon = this.clonarProfundo(agregado);
    let id = (clon.contrato.id || clon.contrato.codigoSesion || '').trim();
    if (!id || id === 'undefined' || id === 'null') {
      id = `MML-${new Date().getFullYear()}-PEND-${Date.now().toString(36).toUpperCase()}`;
      clon.contrato.id = id;
      clon.contrato.codigoSesion = id;
    } else {
      clon.contrato.id = id;
      if (!clon.contrato.codigoSesion) {
        clon.contrato.codigoSesion = id;
      }
    }
    const ops = crearOperacionesSplitsVacias();
    ops.contratosUpsert[id] = clon;

    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    const tituloObra = clon.contrato.tituloObra?.trim() || clon.contrato.codigoSesion || id;
    this.registrarTrazaCola(
      online ? 'ENCOLADO_ONLINE' : 'ENCOLADO_OFFLINE',
      online ? `Split Sheet en cola de subida` : `Guardado en cola (sin internet)`,
      `Split Sheet "${tituloObra}" guardado en cola para subirse automáticamente.`,
      id
    );

    this.encolarOperaciones(ops);
  }

  encolarEliminarContrato(contratoId: string, tituloObra?: string): void {
    const ops = crearOperacionesSplitsVacias();
    ops.contratosEliminarIds.push(contratoId);

    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this.registrarTrazaCola(
      online ? 'ENCOLADO_ONLINE' : 'ENCOLADO_OFFLINE',
      `Archivo de Split Sheet en cola`,
      `Solicitud para archivar "${tituloObra || contratoId}" guardada en cola.`,
      contratoId
    );

    this.encolarOperaciones(ops);
  }

  encolarRestaurarContrato(contratoId: string): void {
    const ops = crearOperacionesSplitsVacias();
    ops.contratosRestaurarIds.push(contratoId);
    this.encolarOperaciones(ops);
  }

  encolarVersionSnapshot(version: SnapshotVersionPendienteSplits): void {
    const ops = crearOperacionesSplitsVacias();
    ops.versionesCrear.push(this.clonarProfundo(version));
    this.encolarOperaciones(ops);
  }

  encolarEventoTrazabilidad(evento: EventoTrazabilidadEntity): void {
    const ops = crearOperacionesSplitsVacias();
    ops.eventosTrazabilidad.push(this.clonarProfundo(evento));
    this.encolarOperaciones(ops);
  }

  encolarUpsertDirectorio(items: ParticipanteDirectorioEntity[]): void {
    if (!items || items.length === 0) return;
    const ops = crearOperacionesSplitsVacias();
    for (const item of items) {
      const docNorm = normalizarNumeroDocumento(item.numeroDocumento || item.id);
      if (!docNorm) continue;
      ops.directorioUpsert[docNorm] = this.clonarProfundo({
        ...item,
        id: docNorm,
        numeroDocumento: docNorm
      });
    }
    this.encolarOperaciones(ops);
  }

  encolarEliminarDirectorio(id: string): void {
    const docNorm = normalizarNumeroDocumento(id);
    if (!docNorm) return;
    const ops = crearOperacionesSplitsVacias();
    ops.directorioEliminarIds.push(docNorm);
    this.encolarOperaciones(ops);
  }

  private encolarOperaciones(nuevas: OperacionesSincronizacionSplits): void {
    if (esOperacionesSplitsVacias(nuevas)) return;

    const ahoraIso = new Date().toISOString();
    const colaActual = [...this.colaInterna()];

    if (colaActual.length > 0 && (!this.procesando || colaActual.length > 1)) {
      const idxUltimo = colaActual.length - 1;
      const ultimo = colaActual[idxUltimo];
      const fusionadas = this.fusionarOperaciones(ultimo.operaciones, nuevas);
      colaActual[idxUltimo] = {
        ...ultimo,
        actualizadoEnIso: ahoraIso,
        operaciones: fusionadas
      };
      this.persistirCola(colaActual);
    } else {
      const nuevoItem: ItemColaSplitsSincronizacion = {
        id: `outbox-splits-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        creadoEnIso: ahoraIso,
        actualizadoEnIso: ahoraIso,
        intentos: 0,
        operaciones: this.clonarProfundo(nuevas)
      };
      colaActual.push(nuevoItem);
      this.persistirCola(colaActual);
    }

    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this.enLinea.set(online);
    if (online) {
      void this.procesarCola(false);
    }
  }

  private fusionarOperaciones(
    base: OperacionesSincronizacionSplits,
    entrante: OperacionesSincronizacionSplits
  ): OperacionesSincronizacionSplits {
    const res: OperacionesSincronizacionSplits = {
      contratosUpsert: { ...(base.contratosUpsert || {}) },
      contratosEliminarIds: [...(base.contratosEliminarIds || [])],
      contratosRestaurarIds: [...(base.contratosRestaurarIds || [])],
      versionesCrear: [...(base.versionesCrear || [])],
      eventosTrazabilidad: [...(base.eventosTrazabilidad || [])],
      directorioUpsert: { ...(base.directorioUpsert || {}) },
      directorioEliminarIds: [...(base.directorioEliminarIds || [])]
    };

    for (const [id, contratoCompleto] of Object.entries(entrante.contratosUpsert || {})) {
      const previo = res.contratosUpsert[id];
      const eventosCombinados = this.combinarEventosTrazabilidad(
        previo?.eventosTrazabilidad || [],
        contratoCompleto.eventosTrazabilidad || []
      );
      res.contratosUpsert[id] = {
        ...this.clonarProfundo(contratoCompleto),
        eventosTrazabilidad: eventosCombinados
      };
      res.contratosEliminarIds = res.contratosEliminarIds.filter((x) => x !== id);
    }

    for (const id of entrante.contratosEliminarIds || []) {
      if (res.contratosUpsert[id]) {
        res.contratosUpsert[id] = {
          ...res.contratosUpsert[id],
          contrato: {
            ...res.contratosUpsert[id].contrato,
            eliminado: true,
            actualizadoEn: new Date().toISOString()
          }
        };
      }
      if (!res.contratosEliminarIds.includes(id)) {
        res.contratosEliminarIds.push(id);
      }
      res.contratosRestaurarIds = res.contratosRestaurarIds.filter((x) => x !== id);
    }

    for (const id of entrante.contratosRestaurarIds || []) {
      if (res.contratosUpsert[id]) {
        res.contratosUpsert[id] = {
          ...res.contratosUpsert[id],
          contrato: {
            ...res.contratosUpsert[id].contrato,
            eliminado: false,
            actualizadoEn: new Date().toISOString()
          }
        };
      }
      if (!res.contratosRestaurarIds.includes(id)) {
        res.contratosRestaurarIds.push(id);
      }
      res.contratosEliminarIds = res.contratosEliminarIds.filter((x) => x !== id);
    }

    const versionesMap = new Map<string, SnapshotVersionPendienteSplits>();
    for (const v of res.versionesCrear) {
      versionesMap.set(v.id, v);
    }
    for (const v of entrante.versionesCrear || []) {
      versionesMap.set(v.id, this.clonarProfundo(v));
    }
    res.versionesCrear = Array.from(versionesMap.values());

    res.eventosTrazabilidad = this.combinarEventosTrazabilidad(
      res.eventosTrazabilidad,
      entrante.eventosTrazabilidad || []
    );

    for (const [doc, item] of Object.entries(entrante.directorioUpsert || {})) {
      res.directorioUpsert[doc] = this.clonarProfundo(item);
      res.directorioEliminarIds = res.directorioEliminarIds.filter((x) => x !== doc);
    }

    for (const doc of entrante.directorioEliminarIds || []) {
      delete res.directorioUpsert[doc];
      if (!res.directorioEliminarIds.includes(doc)) {
        res.directorioEliminarIds.push(doc);
      }
    }

    return res;
  }

  private combinarEventosTrazabilidad(
    listaA: EventoTrazabilidadEntity[],
    listaB: EventoTrazabilidadEntity[]
  ): EventoTrazabilidadEntity[] {
    const mapa = new Map<string, EventoTrazabilidadEntity>();
    for (const ev of listaA || []) {
      if (ev?.id) mapa.set(ev.id, ev);
    }
    for (const ev of listaB || []) {
      if (ev?.id) mapa.set(ev.id, this.clonarProfundo(ev));
    }
    return Array.from(mapa.values()).sort((a, b) =>
      (b.timestamp || '').localeCompare(a.timestamp || '')
    );
  }

  // =========================================================================
  // SUPERPOSICIÓN DE COLA PENDIENTE SOBRE ESTADO REMOTO (PROTECCIÓN LOCAL)
  // =========================================================================
  aplicarColaPendienteSobreEstado(estadoBase: EstadoSplitsPersistido): EstadoSplitsPersistido {
    const cola = this.colaInterna();
    if (cola.length === 0) return estadoBase;

    const contratosMap = new Map<string, ContratoSplitEntity>();
    for (const c of estadoBase.contratos || []) {
      contratosMap.set(c.id, { ...c });
    }

    const colabsPorContrato = new Map<string, ColaboradorSplitEntity[]>();
    for (const colab of estadoBase.colaboradores || []) {
      const lista = colabsPorContrato.get(colab.contratoId) || [];
      lista.push({ ...colab });
      colabsPorContrato.set(colab.contratoId, lista);
    }

    const directorioMap = new Map<string, ParticipanteDirectorioEntity>();
    for (const d of estadoBase.directorioParticipantes || []) {
      const docNorm = normalizarNumeroDocumento(d.numeroDocumento || d.id);
      if (docNorm) directorioMap.set(docNorm, { ...d });
    }

    const versionesPorContrato: Record<string, VersionSplitEntity[]> = {
      ...(estadoBase.versionesPorContrato || {})
    };
    const trazabilidadPorContrato: Record<string, EventoTrazabilidadEntity[]> = {
      ...(estadoBase.trazabilidadPorContrato || {})
    };
    const contratosCompletosPorId: Record<string, ContratoSplitCompleto> = {
      ...(estadoBase.contratosCompletosPorId || {})
    };

    for (const item of cola) {
      const op = item.operaciones;

      for (const [id, completo] of Object.entries(op.contratosUpsert || {})) {
        contratosMap.set(id, { ...completo.contrato });
        colabsPorContrato.set(
          id,
          (completo.colaboradores || []).filter((c) => !c.eliminado).map((c) => ({ ...c }))
        );
        contratosCompletosPorId[id] = this.clonarProfundo(completo);

        if (completo.eventosTrazabilidad && completo.eventosTrazabilidad.length > 0) {
          trazabilidadPorContrato[id] = this.combinarEventosTrazabilidad(
            trazabilidadPorContrato[id] || [],
            completo.eventosTrazabilidad
          );
        }

        for (const colab of completo.colaboradores || []) {
          if (colab.eliminado) continue;
          const docNorm = normalizarNumeroDocumento(colab.numeroDocumento);
          const nombreLimpio = (colab.nombreCompleto || '').trim();
          if (docNorm && docNorm.length >= 5 && nombreLimpio) {
            const prev = directorioMap.get(docNorm);
            directorioMap.set(docNorm, {
              id: docNorm,
              tipoDocumento: normalizarTipoDocumento(colab.tipoDocumento || prev?.tipoDocumento),
              numeroDocumento: docNorm,
              nombreCompleto: nombreLimpio,
              nombreArtistico: colab.nombreArtistico?.trim() || prev?.nombreArtistico || '',
              email: colab.email?.trim() || prev?.email || '',
              telefono: colab.telefono?.trim() || prev?.telefono || '',
              rolSugerido: normalizarRolSplitOficial(colab.rolContribucion || prev?.rolSugerido),
              aporteSugerido: colab.aporteCreativo?.trim() || prev?.aporteSugerido || '',
              ipi: colab.ipi?.trim() || prev?.ipi || '',
              obraReferenciaCsv: prev?.obraReferenciaCsv || completo.contrato.tituloObra || '',
              origen: prev?.origen || 'EXPEDIENTE',
              fechaImportacion: prev?.fechaImportacion || item.creadoEnIso,
              actualizadoEn: item.actualizadoEnIso
            });
          }
        }
      }

      for (const id of op.contratosEliminarIds || []) {
        const c = contratosMap.get(id);
        if (c) {
          contratosMap.set(id, { ...c, eliminado: true, actualizadoEn: item.actualizadoEnIso });
        }
        if (contratosCompletosPorId[id]) {
          contratosCompletosPorId[id] = {
            ...contratosCompletosPorId[id],
            contrato: {
              ...contratosCompletosPorId[id].contrato,
              eliminado: true,
              actualizadoEn: item.actualizadoEnIso
            }
          };
        }
      }

      for (const id of op.contratosRestaurarIds || []) {
        const c = contratosMap.get(id);
        if (c) {
          contratosMap.set(id, { ...c, eliminado: false, actualizadoEn: item.actualizadoEnIso });
        }
        if (contratosCompletosPorId[id]) {
          contratosCompletosPorId[id] = {
            ...contratosCompletosPorId[id],
            contrato: {
              ...contratosCompletosPorId[id].contrato,
              eliminado: false,
              actualizadoEn: item.actualizadoEnIso
            }
          };
        }
      }

      for (const v of op.versionesCrear || []) {
        const listaPrev = versionesPorContrato[v.contratoId] || [];
        if (!listaPrev.some((existing) => existing.id === v.id)) {
          const versionOptimista: VersionSplitEntity = {
            id: v.id,
            contratoId: v.contratoId,
            versionNumero: listaPrev.length + 1,
            markdownSnapshot: v.markdown,
            autorCambio: v.autor,
            motivo: v.motivo,
            archivoUrl: v.archivoUrl,
            storageKey: v.storageKey,
            nombreArchivo: v.nombreArchivo,
            tamanoBytes: v.tamanoBytes,
            pendienteSubidaS3: v.pendienteSubidaS3,
            cambios: v.cambios,
            creadoEn: v.creadoEn
          };
          versionesPorContrato[v.contratoId] = [versionOptimista, ...listaPrev];
        }
      }

      for (const ev of op.eventosTrazabilidad || []) {
        trazabilidadPorContrato[ev.contratoId] = this.combinarEventosTrazabilidad(
          trazabilidadPorContrato[ev.contratoId] || [],
          [ev]
        );
      }

      for (const [doc, p] of Object.entries(op.directorioUpsert || {})) {
        directorioMap.set(doc, { ...p });
      }

      for (const doc of op.directorioEliminarIds || []) {
        directorioMap.delete(doc);
      }
    }

    const todosColaboradores: ColaboradorSplitEntity[] = [];
    colabsPorContrato.forEach((lista) => {
      todosColaboradores.push(...lista);
    });

    return {
      contratos: Array.from(contratosMap.values()),
      colaboradores: todosColaboradores,
      directorioParticipantes: Array.from(directorioMap.values()),
      versionesPorContrato,
      trazabilidadPorContrato,
      contratosCompletosPorId,
      catalogos: estadoBase.catalogos,
      actualizadoEnIso: new Date().toISOString()
    };
  }

  obtenerContratoPendienteEnCola(contratoId: string): ContratoSplitCompleto | null {
    if (!contratoId) return null;
    const cola = this.colaInterna();
    let encontrado: ContratoSplitCompleto | null = null;
    for (const item of cola) {
      const c = item.operaciones.contratosUpsert?.[contratoId];
      if (c) {
        encontrado = this.clonarProfundo(c);
      }
    }
    return encontrado;
  }

  // =========================================================================
  // PROCESAMIENTO SECUENCIAL CON SUBIDA S3 PREVIA, CONFIRMACIÓN HTTP 200 Y BACKOFF
  // =========================================================================
  async reintentarAhora(): Promise<void> {
    this.limpiarTimersReintento();
    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this.enLinea.set(online);
    if (!online) {
      this.ultimoError.set(
        'Sigues sin conexión a internet. Tus cambios están guardados en cola y se subirán automáticamente al recuperar la conexión.'
      );
      return;
    }
    await this.procesarCola(true);
  }

  async procesarCola(forzadoManual = false): Promise<void> {
    if (this.procesando) return;

    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this.enLinea.set(online);
    if (!online) {
      this.limpiarTimersReintento();
      return;
    }

    if (forzadoManual) {
      this.limpiarTimersReintento();
    }

    this.procesando = true;
    this.sincronizando.set(true);

    try {
      // Antes de evaluar si la cola de operaciones está vacía, rescatar del snapshot local del dispositivo
      // cualquier Split Sheet creado/firmado en el iPad o que aún figure con pendienteSubidaS3 / blob:
      // aunque no aparezca en la lista de "intentar sincronizar" (STORE_OUTBOX).
      await this.rescatarContratosPendientesDesdeSnapshotLocal();

      if (this.colaInterna().length === 0) {
        this.ultimoError.set(null);
        this.limpiarTimersReintento();
        await this.repararArchivosS3HuerfanosEnIdb(true);
        return;
      }

      const idsItemsIniciales = this.colaInterna().map((it) => it.id);
      let maxIntentosFallidos = 0;

      for (const itemId of idsItemsIniciales) {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          this.enLinea.set(false);
          break;
        }

        const itemActual = this.colaInterna().find((it) => it.id === itemId);
        if (!itemActual || esOperacionesSplitsVacias(itemActual.operaciones)) {
          this.persistirCola(this.colaInterna().filter((it) => it.id !== itemId));
          continue;
        }

        try {
          await this.despacharOperacionesItemConConfirmacionParcial(itemActual.id);

          const itemPost = this.colaInterna().find((it) => it.id === itemActual.id);
          if (!itemPost || esOperacionesSplitsVacias(itemPost.operaciones)) {
            const colaRestante = this.colaInterna().filter((it) => it.id !== itemActual.id);
            this.persistirCola(colaRestante);
          }

          this.ultimoError.set(null);
          this.ultimaSincronizacionExitosaIso.set(new Date().toISOString());

          this.registrarTrazaCola(
            'SINCRONIZADO_OK',
            'Cambios subidos correctamente',
            `Se subieron los cambios pendientes (${this.construirEtiquetaItem(itemActual.operaciones)}).`
          );

          this.notificarPostSincronizacion();
        } catch (err) {
          const mensajeError = this.formatearErrorHttp(err);
          const ahoraIso = new Date().toISOString();

          const colaActualizada = [...this.colaInterna()];
          const idx = colaActualizada.findIndex((it) => it.id === itemActual.id);
          const intentos = idx >= 0 ? (colaActualizada[idx].intentos || 0) + 1 : 1;
          maxIntentosFallidos = Math.max(maxIntentosFallidos, intentos);

          if (idx >= 0) {
            if (esOperacionesSplitsVacias(colaActualizada[idx].operaciones)) {
              colaActualizada.splice(idx, 1);
            } else {
              colaActualizada[idx] = {
                ...colaActualizada[idx],
                intentos,
                ultimoIntentoIso: ahoraIso,
                ultimoError: mensajeError
              };
            }
            this.persistirCola(colaActualizada);
          }

          this.ultimoError.set(mensajeError);

          this.registrarTrazaCola(
            'ERROR_REINTENTO',
            `Intento ${intentos} pendiente de confirmación`,
            mensajeError
          );

          // Notificar si se lograron sincronizar otros contratos parcialmente dentro del lote
          this.notificarPostSincronizacion();
        }
      }

      if (this.colaInterna().length > 0 && maxIntentosFallidos > 0) {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          this.enLinea.set(false);
          this.limpiarTimersReintento();
        } else {
          this.programarReintentoConBackoff(maxIntentosFallidos);
        }
      } else {
        await this.repararArchivosS3HuerfanosEnIdb(true);
      }
    } finally {
      this.procesando = false;
      this.sincronizando.set(false);
    }
  }

  /**
   * Analiza el snapshot local del dispositivo (STORE_SNAPSHOT: contratos y contratosCompletosPorId)
   * frente a la base de datos de producción para rescatar e incorporar automáticamente a la cola:
   * 1. Contratos que en el iPad aparecen creados/firmados pero no existen en el servidor.
   * 2. Contratos creados offline cuyo ID colisionó con otro acuerdo distinto en el servidor.
   * 3. Contratos que en el iPad están firmados (CON_FIRMAS / archivoFirmado) o tienen más firmas
   *    que la versión actualmente guardada en el servidor.
   * 4. Contratos cuyo documento vigente o histórico aún aparece "En cola" (pendienteSubidaS3: true o URL blob:),
   *    incluso si ya no figuraban en la lista de "intentar sincronizar" (STORE_OUTBOX).
   */
  private async rescatarContratosPendientesDesdeSnapshotLocal(): Promise<void> {
    const snapshot = await this.cargarSnapshotOffline();
    if (!snapshot) return;

    let contratosRemotos: ContratoSplitEntity[] = [];
    let colaboradoresRemotos: ColaboradorSplitEntity[] = [];

    try {
      const resp = await firstValueFrom(
        this.http
          .get<{
            success?: boolean;
            data?: {
              datos?: {
                contratos?: ContratoSplitEntity[];
                colaboradores?: ColaboradorSplitEntity[];
              };
              contratos?: ContratoSplitEntity[];
              colaboradores?: ColaboradorSplitEntity[];
            };
          }>(`${this.apiUrl}/estado?incluirEliminados=true`)
          .pipe(timeout(15000))
      );
      const contenedor = resp?.data?.datos || resp?.data;
      if (Array.isArray(contenedor?.contratos)) {
        contratosRemotos = contenedor!.contratos!;
      } else {
        return;
      }
      if (Array.isArray(contenedor?.colaboradores)) {
        colaboradoresRemotos = contenedor!.colaboradores!;
      }
    } catch {
      return;
    }

    const mapaRemotos = new Map<string, ContratoSplitEntity>(
      contratosRemotos.filter((c) => !!c?.id).map((c) => [c.id, c])
    );

    const mapaCatalogoLocal = new Map<string, ContratoSplitCompleto>();

    for (const [idKey, comp] of Object.entries(snapshot.contratosCompletosPorId || {})) {
      if (!comp?.contrato) continue;
      const id = (comp.contrato.id || idKey || '').trim();
      if (!id) continue;
      mapaCatalogoLocal.set(id, this.clonarProfundo(comp));
    }

    for (const c of snapshot.contratos || []) {
      if (!c?.id) continue;
      const existente = mapaCatalogoLocal.get(c.id);
      const colabsContrato = (snapshot.colaboradores || []).filter(
        (col) => col.contratoId === c.id && !col.eliminado
      );
      if (!existente) {
        mapaCatalogoLocal.set(c.id, {
          contrato: this.clonarProfundo(c),
          colaboradores: this.clonarProfundo(colabsContrato),
          editoras: [],
          condicionesEconomicas: {
            id: `cond-${c.id}`,
            contratoId: c.id,
            moneda: 'COP',
            eliminado: false
          },
          versiones: this.clonarProfundo(snapshot.versionesPorContrato?.[c.id] || []),
          eventosTrazabilidad: this.clonarProfundo(snapshot.trazabilidadPorContrato?.[c.id] || [])
        });
      } else {
        // Conservar el estado más completo entre contratos[] y contratosCompletosPorId[id]
        if (
          (c.estado === 'CON_FIRMAS' && existente.contrato.estado !== 'CON_FIRMAS') ||
          (c.archivoFirmado && !existente.contrato.archivoFirmado) ||
          (c.actualizadoEn || '') > (existente.contrato.actualizadoEn || '')
        ) {
          existente.contrato = this.clonarProfundo(c);
        }
        if (colabsContrato.length > (existente.colaboradores?.length || 0)) {
          existente.colaboradores = this.clonarProfundo(colabsContrato);
        }
      }
    }

    if (mapaCatalogoLocal.size === 0) return;

    const yaEnColaIds = this.contratosPendientesIds();
    const opsRescate = crearOperacionesSplitsVacias();
    let huboRescate = false;

    for (const [idLocal, agregadoLocal] of mapaCatalogoLocal.entries()) {
      if (yaEnColaIds.has(idLocal)) continue;

      const cLocal = agregadoLocal.contrato;
      if (!cLocal || cLocal.eliminado) continue;

      const colabsLocal = agregadoLocal.colaboradores || [];
      const tieneContenido =
        Boolean(cLocal.tituloObra?.trim()) ||
        cLocal.estado === 'CON_FIRMAS' ||
        Boolean(cLocal.archivoFirmado) ||
        colabsLocal.some((col) => Boolean(col.nombreCompleto?.trim() || col.numeroDocumento?.trim() || col.firma));

      if (!tieneContenido) continue;

      const remoto = mapaRemotos.get(idLocal);
      const tituloLocal = (cLocal.tituloObra || '').trim().toLowerCase();
      const tituloRemoto = (remoto?.tituloObra || '').trim().toLowerCase();
      const tsCreacionLocal = Date.parse(cLocal.creadoEn || '');
      const tsCreacionRemoto = Date.parse(remoto?.creadoEn || '');
      const difCreacionMs =
        !Number.isNaN(tsCreacionLocal) && !Number.isNaN(tsCreacionRemoto)
          ? Math.abs(tsCreacionLocal - tsCreacionRemoto)
          : 0;

      const noExisteEnServidor = !remoto;
      const esColisionIdDistintaObra =
        !!remoto &&
        difCreacionMs > 180_000 &&
        tituloLocal.length > 0 &&
        tituloRemoto.length > 0 &&
        tituloLocal !== tituloRemoto;

      const firmasLocalesCount = colabsLocal.filter((col) => !!col.firma).length;
      const firmasRemotasCount = colaboradoresRemotos.filter(
        (col) => col.contratoId === idLocal && !!col.firma
      ).length;

      const localMasAvanzadoEnFirmas =
        !!remoto &&
        !esColisionIdDistintaObra &&
        ((cLocal.estado === 'CON_FIRMAS' && remoto.estado !== 'CON_FIRMAS') ||
          (!!cLocal.archivoFirmado && !remoto.archivoFirmado) ||
          firmasLocalesCount > firmasRemotasCount);

      const afLocal = cLocal.archivoFirmado;
      const histLocal = Array.isArray(cLocal.archivosFirmadosHistorico)
        ? cLocal.archivosFirmadosHistorico
        : [];
      const tieneArchivoPendienteS3Local =
        (!!afLocal && (afLocal.pendienteSubidaS3 || !afLocal.url || afLocal.url.startsWith('blob:'))) ||
        histLocal.some((h) => !!h && (h.pendienteSubidaS3 || !h.url || h.url.startsWith('blob:')));

      if (
        noExisteEnServidor ||
        esColisionIdDistintaObra ||
        localMasAvanzadoEnFirmas ||
        tieneArchivoPendienteS3Local
      ) {
        opsRescate.contratosUpsert[idLocal] = this.clonarProfundo(agregadoLocal);
        huboRescate = true;

        const versionesLocales =
          snapshot.versionesPorContrato?.[idLocal] || agregadoLocal.versiones || [];
        if (
          (noExisteEnServidor || esColisionIdDistintaObra || localMasAvanzadoEnFirmas) &&
          Array.isArray(versionesLocales)
        ) {
          for (const v of versionesLocales) {
            if (!v?.id) continue;
            opsRescate.versionesCrear.push({
              id: v.id,
              contratoId: idLocal,
              markdown: v.markdownSnapshot || cLocal.markdownCompilado || '',
              autor: v.autorCambio || 'Equipo Medellín Music Lab',
              motivo: v.motivo || 'Cierre de firmas electrónicas en plataforma',
              archivoUrl: v.archivoUrl,
              storageKey: v.storageKey,
              nombreArchivo: v.nombreArchivo,
              tamanoBytes: v.tamanoBytes,
              idArchivoCola: cLocal.archivoFirmado?.idArchivoCola,
              pendienteSubidaS3: v.pendienteSubidaS3 ?? cLocal.archivoFirmado?.pendienteSubidaS3,
              cambios: v.cambios,
              creadoEn: v.creadoEn || cLocal.actualizadoEn || new Date().toISOString()
            });
          }
        }
      }
    }

    if (huboRescate) {
      const colaActual = [...this.colaInterna()];
      const ahoraIso = new Date().toISOString();
      if (colaActual.length > 0) {
        const idxUltimo = colaActual.length - 1;
        const combinadas = this.fusionarOperaciones(colaActual[idxUltimo].operaciones, opsRescate);
        colaActual[idxUltimo] = {
          ...colaActual[idxUltimo],
          actualizadoEnIso: ahoraIso,
          operaciones: combinadas
        };
      } else {
        colaActual.push({
          id: `q-splits-rescue-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          creadoEnIso: ahoraIso,
          actualizadoEnIso: ahoraIso,
          intentos: 0,
          operaciones: opsRescate
        });
      }
      this.persistirCola(colaActual);
    }
  }

  /**
   * Auto-repara elementos de la cola antes de sincronizar:
   * - Asigna un consecutivo oficial (MML-YYYY-XXXX) a contratos que se hayan creado offline sin ID
   *   o cuyo ID colisione con un contrato remoto distinto por haberse iniciado sin caché previa.
   * - Vincula o sanea versiones y eventos de trazabilidad que tengan contratoId faltante.
   */
  private async repararOperacionesItemAntesDeSincronizar(itemId: string): Promise<void> {
    const item = this.colaInterna().find((it) => it.id === itemId);
    if (!item) return;

    let contratosRemotos: Array<{
      id: string;
      codigoSesion?: string;
      tituloObra?: string;
      creadoEn?: string;
    }> = [];

    try {
      const resp = await firstValueFrom(
        this.http
          .get<{
            success?: boolean;
            data?: {
              datos?: {
                contratos?: Array<{
                  id: string;
                  codigoSesion?: string;
                  tituloObra?: string;
                  creadoEn?: string;
                }>;
              };
              contratos?: Array<{
                id: string;
                codigoSesion?: string;
                tituloObra?: string;
                creadoEn?: string;
              }>;
            };
          }>(`${this.apiUrl}/estado?incluirEliminados=true`)
          .pipe(timeout(12000))
      );
      const listaRemota = resp?.data?.datos?.contratos || resp?.data?.contratos;
      if (Array.isArray(listaRemota)) {
        contratosRemotos = listaRemota;
      }
    } catch {
      // Si la consulta previa falla, continuamos con los IDs locales disponibles
    }

    const mapaRemotos = new Map(contratosRemotos.map((c) => [c.id, c]));
    const listaParaConsecutivo: Array<{ id?: string; codigoSesion?: string }> = [
      ...contratosRemotos.map((c) => ({ id: c.id, codigoSesion: c.codigoSesion || c.id }))
    ];

    for (const [k, ag] of Object.entries(item.operaciones.contratosUpsert || {})) {
      const idCand = ag?.contrato?.id || k;
      if (idCand && !idCand.includes('-PEND-')) {
        listaParaConsecutivo.push({ id: idCand, codigoSesion: ag?.contrato?.codigoSesion || idCand });
      }
    }

    let huboReparacion = false;
    const opClon = this.clonarProfundo(item.operaciones);
    const mapaReemplazoIds = new Map<string, string>();
    const nuevoContratosUpsert: Record<string, ContratoSplitCompleto> = {};

    for (const [keyOriginal, agregado] of Object.entries(opClon.contratosUpsert || {})) {
      if (!agregado || !agregado.contrato) {
        huboReparacion = true;
        continue;
      }

      const idActual = (agregado.contrato.id || agregado.contrato.codigoSesion || keyOriginal || '').trim();
      const sinIdValido =
        !idActual ||
        idActual === 'undefined' ||
        idActual === 'null' ||
        idActual.includes('-PEND-');

      const remotoExistente = idActual ? mapaRemotos.get(idActual) : undefined;
      const tituloLocal = (agregado.contrato.tituloObra || '').trim().toLowerCase();
      const tituloRemoto = (remotoExistente?.tituloObra || '').trim().toLowerCase();
      const tsLocal = Date.parse(agregado.contrato.creadoEn || '');
      const tsRemoto = Date.parse(remotoExistente?.creadoEn || '');
      const diferenciaCreacionMs =
        !Number.isNaN(tsLocal) && !Number.isNaN(tsRemoto) ? Math.abs(tsLocal - tsRemoto) : 0;

      // Detectar si se creó offline con un ID (ej. MML-2026-0001 o consecutivo en caché desactualizada)
      // que ya pertenece a otro acuerdo distinto en el servidor
      const esColisionOfflinePorDefecto =
        !!remotoExistente &&
        diferenciaCreacionMs > 180_000 &&
        tituloLocal.length > 0 &&
        tituloRemoto.length > 0 &&
        tituloLocal !== tituloRemoto;

      if (sinIdValido || esColisionOfflinePorDefecto) {
        const nuevoId = generarCodigoSesionConsecutivo(listaParaConsecutivo);
        listaParaConsecutivo.push({ id: nuevoId, codigoSesion: nuevoId });

        if (keyOriginal) mapaReemplazoIds.set(keyOriginal, nuevoId);
        if (idActual) mapaReemplazoIds.set(idActual, nuevoId);

        agregado.contrato.id = nuevoId;
        agregado.contrato.codigoSesion = nuevoId;

        if (Array.isArray(agregado.colaboradores)) {
          for (const col of agregado.colaboradores) {
            col.contratoId = nuevoId;
          }
        }
        if (agregado.condicionesEconomicas) {
          agregado.condicionesEconomicas.contratoId = nuevoId;
        }
        if (Array.isArray(agregado.eventosTrazabilidad)) {
          for (const ev of agregado.eventosTrazabilidad) {
            ev.contratoId = nuevoId;
          }
        }

        nuevoContratosUpsert[nuevoId] = agregado;
        huboReparacion = true;
      } else {
        agregado.contrato.id = idActual;
        if (!agregado.contrato.codigoSesion || agregado.contrato.codigoSesion.includes('-PEND-')) {
          agregado.contrato.codigoSesion = idActual;
        }
        if (Array.isArray(agregado.colaboradores)) {
          for (const col of agregado.colaboradores) {
            if (!col.contratoId || col.contratoId !== idActual) {
              col.contratoId = idActual;
              huboReparacion = true;
            }
          }
        }
        if (agregado.condicionesEconomicas && agregado.condicionesEconomicas.contratoId !== idActual) {
          agregado.condicionesEconomicas.contratoId = idActual;
          huboReparacion = true;
        }
        if (Array.isArray(agregado.eventosTrazabilidad)) {
          for (const ev of agregado.eventosTrazabilidad) {
            if (!ev.contratoId || ev.contratoId !== idActual) {
              ev.contratoId = idActual;
              huboReparacion = true;
            }
          }
        }
        nuevoContratosUpsert[idActual] = agregado;
        if (keyOriginal !== idActual) {
          huboReparacion = true;
        }
      }
    }

    opClon.contratosUpsert = nuevoContratosUpsert;
    const idsContratosEnCola = Object.keys(nuevoContratosUpsert);

    // Reparar versiones con contratoId remapeado o vacío
    if (Array.isArray(opClon.versionesCrear)) {
      const versionesSaneadas: SnapshotVersionPendienteSplits[] = [];
      for (const v of opClon.versionesCrear) {
        if (!v) {
          huboReparacion = true;
          continue;
        }
        if (!v.id) {
          v.id = `v-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
          huboReparacion = true;
        }
        if (v.contratoId && mapaReemplazoIds.has(v.contratoId)) {
          v.contratoId = mapaReemplazoIds.get(v.contratoId)!;
          huboReparacion = true;
        }
        if (!v.contratoId || v.contratoId === 'undefined' || v.contratoId === 'null') {
          const matchCola = v.idArchivoCola?.match(/^s3-split-(MML-\d{4}-\d{4,})-/i);
          if (matchCola?.[1]) {
            v.contratoId = mapaReemplazoIds.get(matchCola[1]) || matchCola[1];
            huboReparacion = true;
          } else if (idsContratosEnCola.length === 1) {
            v.contratoId = idsContratosEnCola[0];
            huboReparacion = true;
          }
        }
        if (v.contratoId && v.contratoId !== 'undefined' && v.contratoId !== 'null') {
          versionesSaneadas.push(v);
        } else {
          huboReparacion = true;
        }
      }
      opClon.versionesCrear = versionesSaneadas;
    }

    // Reparar eventos de trazabilidad con contratoId remapeado o vacío
    if (Array.isArray(opClon.eventosTrazabilidad)) {
      const eventosSaneados: EventoTrazabilidadEntity[] = [];
      for (const ev of opClon.eventosTrazabilidad) {
        if (!ev) {
          huboReparacion = true;
          continue;
        }
        if (!ev.id) {
          ev.id = `ev-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
          huboReparacion = true;
        }
        if (ev.contratoId && mapaReemplazoIds.has(ev.contratoId)) {
          ev.contratoId = mapaReemplazoIds.get(ev.contratoId)!;
          huboReparacion = true;
        }
        if (!ev.contratoId || ev.contratoId === 'undefined' || ev.contratoId === 'null') {
          const idColaEv = String(ev.metadata?.['idArchivoCola'] || '');
          const matchCola = idColaEv.match(/^s3-split-(MML-\d{4}-\d{4,})-/i);
          if (matchCola?.[1]) {
            ev.contratoId = mapaReemplazoIds.get(matchCola[1]) || matchCola[1];
            huboReparacion = true;
          } else if (idsContratosEnCola.length === 1) {
            ev.contratoId = idsContratosEnCola[0];
            huboReparacion = true;
          }
        }
        if (ev.contratoId && ev.contratoId !== 'undefined' && ev.contratoId !== 'null') {
          eventosSaneados.push(ev);
        } else {
          huboReparacion = true;
        }
      }
      opClon.eventosTrazabilidad = eventosSaneados;
    }

    if (huboReparacion) {
      const cola = [...this.colaInterna()];
      const idx = cola.findIndex((it) => it.id === itemId);
      if (idx >= 0) {
        cola[idx] = {
          ...cola[idx],
          actualizadoEnIso: new Date().toISOString(),
          operaciones: opClon
        };
        this.persistirCola(cola);
      }

      if (mapaReemplazoIds.size > 0) {
        await this.aplicarRemapeoIdsEnSnapshotLocal(mapaReemplazoIds, nuevoContratosUpsert);
      }
    }
  }

  private async aplicarRemapeoIdsEnSnapshotLocal(
    mapaReemplazoIds: Map<string, string>,
    nuevoContratosUpsert: Record<string, ContratoSplitCompleto>
  ): Promise<void> {
    const snapshot = await this.cargarSnapshotOffline();
    if (!snapshot) return;

    const idsViejos = new Set(mapaReemplazoIds.keys());
    const contratosFiltrados = (snapshot.contratos || []).filter((c) => !idsViejos.has(c.id));
    const colaboradoresFiltrados = (snapshot.colaboradores || []).filter(
      (col) => !idsViejos.has(col.contratoId)
    );
    const completos = { ...(snapshot.contratosCompletosPorId || {}) };
    const versiones = { ...(snapshot.versionesPorContrato || {}) };
    const trazabilidad = { ...(snapshot.trazabilidadPorContrato || {}) };

    for (const [idViejo, idNuevo] of mapaReemplazoIds.entries()) {
      delete completos[idViejo];
      if (versiones[idViejo]) {
        versiones[idNuevo] = versiones[idViejo].map((v) => ({ ...v, contratoId: idNuevo }));
        delete versiones[idViejo];
      }
      if (trazabilidad[idViejo]) {
        trazabilidad[idNuevo] = trazabilidad[idViejo].map((e) => ({ ...e, contratoId: idNuevo }));
        delete trazabilidad[idViejo];
      }
    }

    for (const [idNuevo, ag] of Object.entries(nuevoContratosUpsert)) {
      if (!ag?.contrato) continue;
      contratosFiltrados.push(this.clonarProfundo(ag.contrato));
      for (const col of ag.colaboradores || []) {
        colaboradoresFiltrados.push(this.clonarProfundo(col));
      }
      completos[idNuevo] = this.clonarProfundo(ag);
    }

    await this.guardarSnapshotOffline({
      ...snapshot,
      contratos: contratosFiltrados,
      colaboradores: colaboradoresFiltrados,
      contratosCompletosPorId: completos,
      versionesPorContrato: versiones,
      trazabilidadPorContrato: trazabilidad
    });
  }

  private async actualizarContratoEnSnapshotLocal(agregado: ContratoSplitCompleto): Promise<void> {
    if (!agregado?.contrato?.id) return;
    const snapshot = await this.cargarSnapshotOffline();
    if (!snapshot) return;
    const id = agregado.contrato.id;
    await this.guardarSnapshotOffline({
      ...snapshot,
      contratos: [
        ...(snapshot.contratos || []).filter((c) => c.id !== id),
        this.clonarProfundo(agregado.contrato)
      ],
      contratosCompletosPorId: {
        ...(snapshot.contratosCompletosPorId || {}),
        [id]: this.clonarProfundo(agregado)
      }
    });
  }

  /**
   * Ejecuta las operaciones de un item en orden lógico sin bloqueo de cabecera (No Head-of-Line Blocking):
   * primero repara IDs faltantes/colisionados, sube cualquier documento firmado o histórico pendiente a AWS S3
   * (regenerando el PDF firmado si IndexedDB de iOS perdió el Blob), actualiza los enlaces canónicos
   * y confirma cada operación individualmente en PostgreSQL.
   */
  private async despacharOperacionesItemConConfirmacionParcial(itemId: string): Promise<void> {
    await this.repararOperacionesItemAntesDeSincronizar(itemId);

    const obtenerItemActual = (): ItemColaSplitsSincronizacion | undefined =>
      this.colaInterna().find((it) => it.id === itemId);

    const actualizarOperacionesItem = (mutador: (op: OperacionesSincronizacionSplits) => void): void => {
      const cola = [...this.colaInterna()];
      const idx = cola.findIndex((it) => it.id === itemId);
      if (idx < 0) return;
      const opClon = this.clonarProfundo(cola[idx].operaciones);
      mutador(opClon);
      cola[idx] = {
        ...cola[idx],
        actualizadoEnIso: new Date().toISOString(),
        operaciones: opClon
      };
      this.persistirCola(cola);
    };

    const erroresParciales: unknown[] = [];
    const mapaUrlsResueltasPorColaId = new Map<string, { urlCompleta: string; storageKey: string }>();
    const mapaUrlsFirmadosPorContratoId = new Map<
      string,
      { urlCompleta: string; storageKey: string; nombreArchivo?: string; tamanoBytes?: number }
    >();

    // 1. Upsert de Contratos Split Sheet completos (subiendo primero archivos firmados y su histórico a S3)
    const item1 = obtenerItemActual();
    for (const [contratoId, agregadoOriginal] of Object.entries(item1?.operaciones.contratosUpsert || {})) {
      try {
        const agregado = this.clonarProfundo(agregadoOriginal);
        const af = agregado.contrato.archivoFirmado;

        const requiereSubidaAf =
          !!af &&
          (af.pendienteSubidaS3 ||
            !af.url ||
            af.url.startsWith('blob:') ||
            (!af.url.startsWith('http') && !!af.dataBase64));

        if (af && requiereSubidaAf) {
          let subido = await this.resolverSubidaS3PendientePorId(
            af.idArchivoCola,
            af.dataBase64,
            contratoId,
            af.nombreArchivo,
            'mml-splits/firmados'
          );

          if (!subido) {
            try {
              const nombreRegen =
                af.nombreArchivo ||
                `Split_Sheet_Firmado_${(agregado.contrato.tituloObra || contratoId).replace(/[^a-zA-Z0-9_-]+/g, '_')}.pdf`;
              const infoPdfRegen = this.pdfExporter.generarPdfBlob(agregado, nombreRegen);
              const nombreUnicoRegen = `${contratoId}_${Date.now()}_${nombreRegen.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
              subido = await this.ejecutarUploadS3ConProgreso(
                infoPdfRegen.blob,
                nombreUnicoRegen,
                'mml-splits/firmados',
                nombreRegen
              );
              if (af.idArchivoCola && subido) {
                this.cacheUrlsS3SubidasPorColaId.set(af.idArchivoCola, subido);
                await this.eliminarArchivoS3PendienteDeIdb(af.idArchivoCola);
              }
            } catch {
              // Si falla la subida del PDF regenerado, se reintentará en el próximo ciclo
            }
          }

          if (subido) {
            if (af.idArchivoCola) {
              mapaUrlsResueltasPorColaId.set(af.idArchivoCola, subido);
            }
            af.url = subido.urlCompleta;
            af.storageKey = subido.storageKey;
            af.pendienteSubidaS3 = false;
            delete af.dataBase64;
            mapaUrlsFirmadosPorContratoId.set(contratoId, {
              urlCompleta: subido.urlCompleta,
              storageKey: subido.storageKey,
              nombreArchivo: af.nombreArchivo,
              tamanoBytes: af.tamanoBytes
            });
          }
        } else if (af && af.url && af.url.startsWith('http')) {
          delete af.dataBase64;
          if (af.idArchivoCola) {
            mapaUrlsResueltasPorColaId.set(af.idArchivoCola, {
              urlCompleta: af.url,
              storageKey: af.storageKey || af.url
            });
          }
          mapaUrlsFirmadosPorContratoId.set(contratoId, {
            urlCompleta: af.url,
            storageKey: af.storageKey || af.url,
            nombreArchivo: af.nombreArchivo,
            tamanoBytes: af.tamanoBytes
          });
        }

        // Subir también cualquier documento dentro de archivosFirmadosHistorico que haya quedado pendiente o con URL blob:
        if (Array.isArray(agregado.contrato.archivosFirmadosHistorico)) {
          for (const h of agregado.contrato.archivosFirmadosHistorico) {
            if (!h) continue;
            const requiereSubidaHist =
              h.pendienteSubidaS3 ||
              !h.url ||
              h.url.startsWith('blob:') ||
              (!h.url.startsWith('http') && !!h.dataBase64);

            if (h.idArchivoCola && mapaUrlsResueltasPorColaId.has(h.idArchivoCola)) {
              const subido = mapaUrlsResueltasPorColaId.get(h.idArchivoCola)!;
              h.url = subido.urlCompleta;
              h.storageKey = subido.storageKey;
              h.pendienteSubidaS3 = false;
              delete h.dataBase64;
            } else if (requiereSubidaHist) {
              let subidoHist = await this.resolverSubidaS3PendientePorId(
                h.idArchivoCola,
                h.dataBase64,
                contratoId,
                h.nombreArchivo,
                'mml-splits/firmados'
              );

              if (!subidoHist) {
                try {
                  const nombreRegenHist =
                    h.nombreArchivo ||
                    `Split_Sheet_Firmado_${(agregado.contrato.tituloObra || contratoId).replace(/[^a-zA-Z0-9_-]+/g, '_')}_v${h.versionContrato || 1}.pdf`;
                  const infoPdfRegenHist = this.pdfExporter.generarPdfBlob(agregado, nombreRegenHist);
                  const nombreUnicoHist = `${contratoId}_${Date.now()}_${nombreRegenHist.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
                  subidoHist = await this.ejecutarUploadS3ConProgreso(
                    infoPdfRegenHist.blob,
                    nombreUnicoHist,
                    'mml-splits/firmados',
                    nombreRegenHist
                  );
                  if (h.idArchivoCola && subidoHist) {
                    this.cacheUrlsS3SubidasPorColaId.set(h.idArchivoCola, subidoHist);
                    await this.eliminarArchivoS3PendienteDeIdb(h.idArchivoCola);
                  }
                } catch {
                  // Conservar para siguiente intento si falla S3
                }
              }

              if (subidoHist) {
                if (h.idArchivoCola) {
                  mapaUrlsResueltasPorColaId.set(h.idArchivoCola, subidoHist);
                }
                h.url = subidoHist.urlCompleta;
                h.storageKey = subidoHist.storageKey;
                h.pendienteSubidaS3 = false;
                delete h.dataBase64;
              }
            } else if (h.url && h.url.startsWith('http')) {
              delete h.dataBase64;
              if (h.idArchivoCola) {
                mapaUrlsResueltasPorColaId.set(h.idArchivoCola, {
                  urlCompleta: h.url,
                  storageKey: h.storageKey || h.url
                });
              }
            }
          }
        }

        if (Array.isArray(agregado.eventosTrazabilidad)) {
          for (const ev of agregado.eventosTrazabilidad) {
            const idColaEv = String(ev.metadata?.['idArchivoCola'] || '');
            if (idColaEv && mapaUrlsResueltasPorColaId.has(idColaEv)) {
              const subido = mapaUrlsResueltasPorColaId.get(idColaEv)!;
              ev.metadata = {
                ...ev.metadata,
                archivoUrl: subido.urlCompleta,
                storageKey: subido.storageKey,
                pendienteSubidaS3: false
              };
            }
          }
        }

        // Persistir de inmediato las URLs S3 resueltas en el item en cola antes de enviar a PostgreSQL
        actualizarOperacionesItem((op) => {
          if (op.contratosUpsert[contratoId]) {
            op.contratosUpsert[contratoId] = this.clonarProfundo(agregado);
          }
        });

        const resp = await firstValueFrom(
          this.http
            .post<{ success?: boolean; message?: string }>(`${this.apiUrl}/contratos`, agregado)
            .pipe(timeout(REQUEST_TIMEOUT_MS))
        );
        if (!resp || resp.success === false) {
          throw new Error(resp?.message || `El servidor no confirmó el guardado del Split Sheet ${contratoId}.`);
        }
        actualizarOperacionesItem((op) => {
          delete op.contratosUpsert[contratoId];
        });
        await this.actualizarContratoEnSnapshotLocal(agregado);
      } catch (errContrato) {
        erroresParciales.push(errContrato);
      }
    }

    // 2. Eliminación lógica de Contratos
    const item2 = obtenerItemActual();
    for (const contratoId of [...(item2?.operaciones.contratosEliminarIds || [])]) {
      try {
        const resp = await firstValueFrom(
          this.http
            .delete<{ success?: boolean; message?: string }>(
              `${this.apiUrl}/contratos/${encodeURIComponent(contratoId)}`
            )
            .pipe(timeout(REQUEST_TIMEOUT_MS))
        );
        if (!resp || resp.success === false) {
          throw new Error(resp?.message || `No se pudo archivar el Split Sheet ${contratoId}.`);
        }
        actualizarOperacionesItem((op) => {
          op.contratosEliminarIds = op.contratosEliminarIds.filter((id) => id !== contratoId);
        });
      } catch (errElim) {
        erroresParciales.push(errElim);
      }
    }

    // 3. Restauración de Contratos
    const item3 = obtenerItemActual();
    for (const contratoId of [...(item3?.operaciones.contratosRestaurarIds || [])]) {
      try {
        const resp = await firstValueFrom(
          this.http
            .post<{ success?: boolean; message?: string }>(
              `${this.apiUrl}/contratos/${encodeURIComponent(contratoId)}/restaurar`,
              {}
            )
            .pipe(timeout(REQUEST_TIMEOUT_MS))
        );
        if (!resp || resp.success === false) {
          throw new Error(resp?.message || `No se pudo restaurar el Split Sheet ${contratoId}.`);
        }
        actualizarOperacionesItem((op) => {
          op.contratosRestaurarIds = op.contratosRestaurarIds.filter((id) => id !== contratoId);
        });
      } catch (errRest) {
        erroresParciales.push(errRest);
      }
    }

    // 4. Snapshots de versiones (subiendo primero su PDF a S3 si estaba en cola offline o con blob:)
    const item4 = obtenerItemActual();
    for (const versionOriginal of [...(item4?.operaciones.versionesCrear || [])]) {
      try {
        const version = this.clonarProfundo(versionOriginal);
        const requiereSubidaVer =
          version.pendienteSubidaS3 || !version.archivoUrl || version.archivoUrl.startsWith('blob:');

        if (version.idArchivoCola && mapaUrlsResueltasPorColaId.has(version.idArchivoCola)) {
          const subido = mapaUrlsResueltasPorColaId.get(version.idArchivoCola)!;
          version.archivoUrl = subido.urlCompleta;
          version.storageKey = subido.storageKey;
          version.pendienteSubidaS3 = false;
        } else if (requiereSubidaVer && version.idArchivoCola) {
          const subido = await this.resolverSubidaS3PendientePorId(
            version.idArchivoCola,
            undefined,
            version.contratoId,
            version.nombreArchivo,
            'mml-splits/versiones'
          );
          if (subido) {
            mapaUrlsResueltasPorColaId.set(version.idArchivoCola, subido);
            version.archivoUrl = subido.urlCompleta;
            version.storageKey = subido.storageKey;
            version.pendienteSubidaS3 = false;
          }
        }

        if (
          (!version.archivoUrl || version.archivoUrl.startsWith('blob:')) &&
          version.contratoId &&
          mapaUrlsFirmadosPorContratoId.has(version.contratoId)
        ) {
          const firmadoContrato = mapaUrlsFirmadosPorContratoId.get(version.contratoId)!;
          version.archivoUrl = firmadoContrato.urlCompleta;
          version.storageKey = firmadoContrato.storageKey;
          version.pendienteSubidaS3 = false;
        }

        // Nunca enviar una URL blob: efímera a PostgreSQL
        if (version.archivoUrl && version.archivoUrl.startsWith('blob:')) {
          version.archivoUrl = undefined;
        }

        const resp = await firstValueFrom(
          this.http
            .post<{ success?: boolean; message?: string }>(`${this.apiUrl}/versiones`, {
              id: version.id,
              contratoId: version.contratoId,
              markdown: version.markdown,
              autor: version.autor,
              motivo: version.motivo,
              archivoUrl: version.archivoUrl,
              storageKey: version.storageKey,
              nombreArchivo: version.nombreArchivo,
              tamanoBytes: version.tamanoBytes,
              cambios: version.cambios,
              creadoEn: version.creadoEn
            })
            .pipe(timeout(REQUEST_TIMEOUT_MS))
        );
        if (!resp || resp.success === false) {
          throw new Error(resp?.message || `No se pudo registrar versión del Split Sheet ${version.contratoId}.`);
        }
        actualizarOperacionesItem((op) => {
          op.versionesCrear = op.versionesCrear.filter((v) => v.id !== version.id);
        });
      } catch (errVer) {
        erroresParciales.push(errVer);
      }
    }

    // 5. Eventos de trazabilidad
    const item5 = obtenerItemActual();
    for (const eventoOriginal of [...(item5?.operaciones.eventosTrazabilidad || [])]) {
      try {
        const evento = this.clonarProfundo(eventoOriginal);
        const idColaEv = String(evento.metadata?.['idArchivoCola'] || '');
        if (idColaEv && mapaUrlsResueltasPorColaId.has(idColaEv)) {
          const subido = mapaUrlsResueltasPorColaId.get(idColaEv)!;
          evento.metadata = {
            ...evento.metadata,
            archivoUrl: subido.urlCompleta,
            storageKey: subido.storageKey,
            pendienteSubidaS3: false
          };
        } else if (
          String(evento.metadata?.['archivoUrl'] || '').startsWith('blob:') &&
          evento.contratoId &&
          mapaUrlsFirmadosPorContratoId.has(evento.contratoId)
        ) {
          const firmadoContrato = mapaUrlsFirmadosPorContratoId.get(evento.contratoId)!;
          evento.metadata = {
            ...evento.metadata,
            archivoUrl: firmadoContrato.urlCompleta,
            storageKey: firmadoContrato.storageKey,
            pendienteSubidaS3: false
          };
        }

        const resp = await firstValueFrom(
          this.http
            .post<{ success?: boolean; message?: string }>(`${this.apiUrl}/trazabilidad`, evento)
            .pipe(timeout(REQUEST_TIMEOUT_MS))
        );
        if (!resp || resp.success === false) {
          throw new Error(resp?.message || `No se pudo registrar evento de trazabilidad ${evento.id}.`);
        }
        actualizarOperacionesItem((op) => {
          op.eventosTrazabilidad = op.eventosTrazabilidad.filter((e) => e.id !== evento.id);
        });
      } catch (errEv) {
        erroresParciales.push(errEv);
      }
    }

    // 6. Upsert de Directorio de Participantes (en bloque)
    const item6 = obtenerItemActual();
    const listaDirectorio = Object.values(item6?.operaciones.directorioUpsert || {});
    if (listaDirectorio.length > 0) {
      try {
        const resp = await firstValueFrom(
          this.http
            .post<{ success?: boolean; message?: string }>(`${this.apiUrl}/directorio/upsert`, {
              items: listaDirectorio
            })
            .pipe(timeout(REQUEST_TIMEOUT_MS))
        );
        if (!resp || resp.success === false) {
          throw new Error(resp?.message || 'No se pudo sincronizar el directorio de participantes.');
        }
        actualizarOperacionesItem((op) => {
          op.directorioUpsert = {};
        });
      } catch (errDirUpsert) {
        erroresParciales.push(errDirUpsert);
      }
    }

    // 7. Eliminación en Directorio de Participantes
    const item7 = obtenerItemActual();
    for (const docNorm of [...(item7?.operaciones.directorioEliminarIds || [])]) {
      try {
        const resp = await firstValueFrom(
          this.http
            .delete<{ success?: boolean; message?: string }>(
              `${this.apiUrl}/directorio/${encodeURIComponent(docNorm)}`
            )
            .pipe(timeout(REQUEST_TIMEOUT_MS))
        );
        if (!resp || resp.success === false) {
          throw new Error(resp?.message || `No se pudo retirar el participante ${docNorm} del directorio.`);
        }
        actualizarOperacionesItem((op) => {
          op.directorioEliminarIds = op.directorioEliminarIds.filter((d) => d !== docNorm);
        });
      } catch (errDirElim) {
        erroresParciales.push(errDirElim);
      }
    }

    if (erroresParciales.length > 0) {
      throw erroresParciales[0];
    }
  }

  /**
   * Rescata documentos PDF que quedaron pendientes en IndexedDB (STORE_ARCHIVOS_S3) o en contratos remotos
   * con pendienteSubidaS3: true / URLs blob: efímeras (como versiones históricas que se movieron antes de subir a S3).
   */
  private reparandoHuerfanos = false;
  async repararArchivosS3HuerfanosEnIdb(desdeProcesarCola = false): Promise<void> {
    if (this.reparandoHuerfanos || (!desdeProcesarCola && this.procesando)) return;
    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    if (!online) return;

    this.reparandoHuerfanos = true;
    try {
      const pendientesIdb = await this.obtenerTodosArchivosS3PendientesEnIdb();
      for (const item of pendientesIdb) {
        if (!item?.id) continue;
        if (!this.cacheUrlsS3SubidasPorColaId.has(item.id)) {
          await this.resolverSubidaS3PendientePorId(
            item.id,
            item.dataBase64,
            item.contratoId,
            item.nombreArchivo,
            item.carpeta || 'mml-splits/firmados'
          );
        }
      }

      // Verificar si algún contrato en el servidor quedó con archivoFirmado o archivosFirmadosHistorico en estado blob:/pendienteSubidaS3
      const respEstado = await firstValueFrom(
        this.http
          .get<{
            success?: boolean;
            data?: {
              datos?: {
                contratos?: ContratoSplitCompleto['contrato'][];
                colaboradores?: ContratoSplitCompleto['colaboradores'];
                condiciones?: ContratoSplitCompleto['condicionesEconomicas'][];
                trazabilidad?: ContratoSplitCompleto['eventosTrazabilidad'];
              };
              contratos?: ContratoSplitCompleto['contrato'][];
              colaboradores?: ContratoSplitCompleto['colaboradores'];
              condiciones?: ContratoSplitCompleto['condicionesEconomicas'][];
              trazabilidad?: ContratoSplitCompleto['eventosTrazabilidad'];
            };
          }>(`${this.apiUrl}/estado?incluirEliminados=true`)
          .pipe(timeout(15000))
      );

      const data = respEstado?.data?.datos || respEstado?.data;
      if (!data || !Array.isArray(data.contratos)) return;

      let huboActualizacionServidor = false;

      for (const contratoRemoto of data.contratos) {
        if (!contratoRemoto?.id) continue;
        const af = contratoRemoto.archivoFirmado;
        const historico = Array.isArray(contratoRemoto.archivosFirmadosHistorico)
          ? contratoRemoto.archivosFirmadosHistorico
          : [];

        const afIncompleto =
          !!af && (af.pendienteSubidaS3 || !af.url || af.url.startsWith('blob:'));
        const histIncompleto = historico.some(
          (h) => !!h && (h.pendienteSubidaS3 || !h.url || h.url.startsWith('blob:'))
        );

        if (!afIncompleto && !histIncompleto) continue;

        const agregadoRemoto: ContratoSplitCompleto = {
          contrato: this.clonarProfundo(contratoRemoto),
          colaboradores: (data.colaboradores || []).filter((c) => c.contratoId === contratoRemoto.id),
          editoras: [],
          condicionesEconomicas:
            (data.condiciones || []).find((c) => c.contratoId === contratoRemoto.id) || {
              id: `ce-${contratoRemoto.id}`,
              contratoId: contratoRemoto.id,
              moneda: 'COP'
            },
          eventosTrazabilidad: (data.trazabilidad || []).filter((e) => e.contratoId === contratoRemoto.id)
        };

        let modificado = false;
        const afClon = agregadoRemoto.contrato.archivoFirmado;
        if (afClon && (afClon.pendienteSubidaS3 || !afClon.url || afClon.url.startsWith('blob:'))) {
          let subidoAf = await this.resolverSubidaS3PendientePorId(
            afClon.idArchivoCola,
            afClon.dataBase64,
            contratoRemoto.id,
            afClon.nombreArchivo,
            'mml-splits/firmados'
          );
          if (!subidoAf) {
            const nombreRegen =
              afClon.nombreArchivo ||
              `Split_Sheet_Firmado_${(contratoRemoto.tituloObra || contratoRemoto.id).replace(/[^a-zA-Z0-9_-]+/g, '_')}.pdf`;
            const infoRegen = this.pdfExporter.generarPdfBlob(agregadoRemoto, nombreRegen);
            const nombreUnicoAf = `${contratoRemoto.id}_${Date.now()}_${nombreRegen.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
            subidoAf = await this.ejecutarUploadS3ConProgreso(
              infoRegen.blob,
              nombreUnicoAf,
              'mml-splits/firmados',
              nombreRegen
            );
          }
          if (subidoAf) {
            afClon.url = subidoAf.urlCompleta;
            afClon.storageKey = subidoAf.storageKey;
            afClon.pendienteSubidaS3 = false;
            delete afClon.dataBase64;
            modificado = true;
          }
        }

        if (Array.isArray(agregadoRemoto.contrato.archivosFirmadosHistorico)) {
          for (const h of agregadoRemoto.contrato.archivosFirmadosHistorico) {
            if (!h || (!h.pendienteSubidaS3 && h.url && !h.url.startsWith('blob:'))) continue;
            let subidoH = await this.resolverSubidaS3PendientePorId(
              h.idArchivoCola,
              h.dataBase64,
              contratoRemoto.id,
              h.nombreArchivo,
              'mml-splits/firmados'
            );
            if (!subidoH) {
              const nombreRegenH =
                h.nombreArchivo ||
                `Split_Sheet_Firmado_${(contratoRemoto.tituloObra || contratoRemoto.id).replace(/[^a-zA-Z0-9_-]+/g, '_')}_v${h.versionContrato || 1}.pdf`;
              const infoRegenH = this.pdfExporter.generarPdfBlob(agregadoRemoto, nombreRegenH);
              const nombreUnicoH = `${contratoRemoto.id}_${Date.now()}_${nombreRegenH.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
              subidoH = await this.ejecutarUploadS3ConProgreso(
                infoRegenH.blob,
                nombreUnicoH,
                'mml-splits/firmados',
                nombreRegenH
              );
            }
            if (subidoH) {
              h.url = subidoH.urlCompleta;
              h.storageKey = subidoH.storageKey;
              h.pendienteSubidaS3 = false;
              delete h.dataBase64;
              modificado = true;
            }
          }
        }

        if (modificado) {
          await firstValueFrom(
            this.http
              .post<{ success?: boolean }>(`${this.apiUrl}/contratos`, agregadoRemoto)
              .pipe(timeout(REQUEST_TIMEOUT_MS))
          );
          await this.actualizarContratoEnSnapshotLocal(agregadoRemoto);
          huboActualizacionServidor = true;
        }
      }

      if (huboActualizacionServidor) {
        this.notificarPostSincronizacion();
      }
    } catch {
      // No interrumpir flujo principal si falla la reparación en segundo plano
    } finally {
      this.reparandoHuerfanos = false;
    }
  }

  private programarReintentoConBackoff(intentos: number): void {
    this.limpiarTimersReintento();

    const exp = Math.min(intentos - 1, 4);
    const delayBase = Math.min(BASE_RETRY_MS * Math.pow(2, exp), MAX_RETRY_MS);
    const jitter = Math.floor(Math.random() * 1000);
    const delayMs = Math.min(delayBase + jitter, MAX_RETRY_MS);

    let segundosRestantes = Math.max(1, Math.ceil(delayMs / 1000));
    this.segundosParaReintento.set(segundosRestantes);

    this.countdownIntervalId = setInterval(() => {
      segundosRestantes = Math.max(0, segundosRestantes - 1);
      this.segundosParaReintento.set(segundosRestantes);
      if (segundosRestantes <= 0 && this.countdownIntervalId) {
        clearInterval(this.countdownIntervalId);
        this.countdownIntervalId = null;
      }
    }, 1000);

    this.retryTimeoutId = setTimeout(() => {
      this.limpiarTimersReintento();
      void this.procesarCola(false);
    }, delayMs);
  }

  private limpiarTimersReintento(): void {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
    if (this.countdownIntervalId) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = null;
    }
    this.segundosParaReintento.set(0);
  }

  private formatearErrorHttp(err: unknown): string {
    if (!err) return 'No se pudieron subir los cambios en este momento. Se reintentará automáticamente.';
    if (typeof err === 'object' && err !== null && (err as { name?: string }).name === 'TimeoutError') {
      return 'La conexión está tardando más de lo normal. Tus cambios siguen seguros en cola y se reintentará automáticamente.';
    }
    if (err instanceof HttpErrorResponse) {
      if (err.status === 0) {
        return 'Sin conexión en este momento. Tus cambios siguen guardados en cola y se subirán automáticamente al conectar.';
      }
      if (err.status === 401 || err.status === 403) {
        return 'Tu sesión expiró o no tienes permisos suficientes. Inicia sesión nuevamente; tus cambios en cola se conservarán.';
      }
      if (err.status === 413) {
        return 'El archivo firmado adjunto supera el tamaño máximo permitido.';
      }
      const msgServer = err.error?.message || err.error?.error;
      return msgServer || 'No se pudieron subir los cambios en este momento. Se reintentará automáticamente.';
    }
    if (err instanceof Error) {
      return err.message;
    }
    return String(err);
  }

  private construirDetalleOperacionesItem(op: OperacionesSincronizacionSplits): string[] {
    const detalles: string[] = [];

    for (const [id, c] of Object.entries(op.contratosUpsert || {})) {
      const titulo = c.contrato.tituloObra?.trim() || 'Obra sin título';
      const codigo = c.contrato.codigoSesion || id;
      const cantColabs = (c.colaboradores || []).filter((col) => !col.eliminado).length;
      const tieneFirmado = c.contrato.estado === 'CON_FIRMAS' || !!c.contrato.archivoFirmado;
      detalles.push(
        `Split Sheet "${titulo}" (${codigo} · ${cantColabs} participante(s)${tieneFirmado ? ' · Firmado' : ''})`
      );
    }

    for (const id of op.contratosEliminarIds || []) {
      detalles.push(`Archivar acuerdo (${id})`);
    }

    for (const id of op.contratosRestaurarIds || []) {
      detalles.push(`Restaurar acuerdo (${id})`);
    }

    for (const _v of op.versionesCrear || []) {
      detalles.push(`Versión de respaldo del PDF`);
    }

    const cantEventos = op.eventosTrazabilidad?.length || 0;
    if (cantEventos > 0) {
      detalles.push(`Actualización de historial (${cantEventos})`);
    }

    const cantDir = Object.keys(op.directorioUpsert || {}).length;
    if (cantDir > 0) {
      detalles.push(`${cantDir} ${cantDir === 1 ? 'participante del directorio' : 'participantes del directorio'}`);
    }

    const cantDirDel = op.directorioEliminarIds?.length || 0;
    if (cantDirDel > 0) {
      detalles.push(`Retirar ${cantDirDel} participante(s) del directorio`);
    }

    return detalles.length > 0 ? detalles : ['Cambios del Split Sheet'];
  }

  private construirEtiquetaItem(op: OperacionesSincronizacionSplits): string {
    const detalles = this.construirDetalleOperacionesItem(op);
    return detalles.join(' · ');
  }

  private clonarProfundo<T>(valor: T): T {
    return JSON.parse(JSON.stringify(valor)) as T;
  }
}
